import React from 'react';
import { Clock, Buildings } from '@phosphor-icons/react';

interface StaleGoalItem {
    employee_name?: string;
    employeeName?: string;
    employee_email?: string;
    employeeEmail?: string;
    department_name?: string;
    departmentName?: string;
    last_updated?: string;
    lastUpdated?: string;
}

interface StaleGoalCardProps {
    data: StaleGoalItem[] | { data: StaleGoalItem[]; metadata?: any; meta?: any; schema?: any };
    options?: {
        showOwner?: boolean;
        showLastUpdate?: boolean;
        showDaysSinceUpdate?: boolean;
        showProgress?: boolean;
        maxCards?: number;
    };
}

const formatDate = (dateStr?: string | null): string | null => {
    if (!dateStr) return null;
    try {
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return null;
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
        return null;
    }
};

const getDaysSince = (dateStr?: string | null): number | null => {
    if (!dateStr) return null;
    try {
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return null;
        const now = new Date();
        const diff = now.getTime() - date.getTime();
        return Math.floor(diff / (1000 * 60 * 60 * 24));
    } catch {
        return null;
    }
};

const getStalenessConfig = (days: number | null) => {
    if (days === null) return { color: 'bg-gray-100 text-gray-600 border border-gray-200', label: 'Unknown' };
    if (days > 60) return { color: 'bg-red-100 text-red-700 border border-red-200', label: `${days}d ago` };
    if (days >= 30) return { color: 'bg-amber-100 text-amber-700 border border-amber-200', label: `${days}d ago` };
    return { color: 'bg-green-100 text-green-700 border border-green-200', label: `${days}d ago` };
};

export const StaleGoalCard: React.FC<StaleGoalCardProps> = ({ data, options = {} }) => {
    const {
        showLastUpdate = true,
        showDaysSinceUpdate = true,
        maxCards = 10,
    } = options;

    let items: StaleGoalItem[] = [];
    if (Array.isArray(data)) {
        items = data;
    } else if (data && typeof data === 'object' && 'data' in data && Array.isArray(data.data)) {
        items = data.data;
    }

    const displayItems = items.slice(0, maxCards);

    if (!displayItems || displayItems.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center h-full text-gray-400 p-6">
                <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                    <Clock size={28} className="text-gray-400" />
                </div>
                <span className="text-sm font-medium">No stale goals found</span>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-2.5 p-3 h-full overflow-auto">
            {displayItems.map((item, index) => {
                const name = item.employeeName || item.employee_name || 'Unknown';
                const department = item.departmentName || item.department_name || '';
                const lastUpdated = item.lastUpdated || item.last_updated || '';
                const formattedDate = formatDate(lastUpdated);
                const daysSince = getDaysSince(lastUpdated);
                const stalenessConfig = getStalenessConfig(daysSince);

                return (
                    <div
                        key={`stale-${index}`}
                        className="group bg-white rounded-lg border border-gray-200 border-l-4 border-l-amber-400 p-3.5 hover:border-amber-300 hover:shadow-sm transition-all cursor-pointer"
                    >
                        {/* Row 1: Name + Staleness badge */}
                        <div className="flex items-start justify-between gap-3 mb-2.5">
                            <h3 className="text-sm font-semibold text-gray-900 leading-snug line-clamp-2 group-hover:text-amber-600 transition-colors flex-1">
                                {name}
                            </h3>
                            {showDaysSinceUpdate && daysSince !== null && (
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide flex-shrink-0 ${stalenessConfig.color}`}>
                                    <Clock size={10} weight="bold" />
                                    {stalenessConfig.label}
                                </span>
                            )}
                        </div>

                        {/* Row 2: Department */}
                        {department && (
                            <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-3">
                                <Buildings size={14} className="text-gray-400" />
                                <span>{department}</span>
                            </div>
                        )}

                        {/* Row 3: Footer */}
                        {showLastUpdate && formattedDate && (
                            <div className="flex items-center text-xs text-gray-500 pt-2.5 border-t border-gray-100">
                                <div className="flex items-center gap-1.5">
                                    <Clock size={14} className="text-gray-400" />
                                    <span>Last updated: {formattedDate}</span>
                                </div>
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};
